import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { apiService } from '../services/api';
import storageService from '../services/storageService';
import type { Account } from '../types/account';
import { useAuth } from './AuthContext';

interface AccountContextType {
  account: Account | null;
  hasMissingAccountInfo: boolean;
  setHasMissingAccountInfo: (value: boolean) => void;
  isFirstLogin: boolean;
  setIsFirstLogin: (value: boolean) => void;
  isAccountLoading: boolean;
  refreshUserDetails: (silent?: boolean) => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

export const AccountContext = createContext<AccountContextType | null>(null);

export const AccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [hasMissingAccountInfo, setHasMissingAccountInfo] = useState<boolean>(false);
  const [isFirstLogin, setIsFirstLogin] = useState<boolean>(false);
  const [fetchedUid, setFetchedUid] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  // It's loading if we are actively fetching, or if we have a user but haven't fetched their account yet.
  const isAccountLoading = isFetching || (!!user && fetchedUid !== user.uid);

  const refreshUserDetails = useCallback(
    async (silent = false) => {
      if (user) {
        try {
          if (!silent) {
            setIsFetching(true);
          }
          const accountDetailsResponse = await apiService.getAccountDetails();

          if (accountDetailsResponse?.success && accountDetailsResponse.account) {
            const newAccount: Account = {
              ...accountDetailsResponse.account,
              actualDefaultBucketName: accountDetailsResponse.account.actualDefaultBucketName,
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              emailVerified: user.emailVerified,
              phoneNumber: user.phoneNumber,
            } as Account;
            setAccount(newAccount);
            setFetchedUid(user.uid);
          } else {
            throw new Error(accountDetailsResponse.message || 'Failed to refresh account details.');
          }
        } catch (_error) {
          await logout();
        } finally {
          if (!silent) {
            setIsFetching(false);
          }
        }
      }
    },
    [user, logout]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchInitialAccount = async () => {
      if (!user) {
        setAccount(null);
        setFetchedUid(null);
        setIsFetching(false);
        return;
      }

      if (account?.uid === user.uid && fetchedUid === user.uid) {
        setIsFetching(false);
        return;
      }

      setIsFetching(true);
      try {
        const accountDetailsResponse = await apiService.getAccountDetails();
        if (!isMounted) {
          return;
        }

        if (accountDetailsResponse?.success && accountDetailsResponse.account) {
          const accountData = accountDetailsResponse.account;

          let periodEnd: Date | null = null;
          if (accountData.subscription?.current_period_end) {
            periodEnd =
              typeof accountData.subscription.current_period_end === 'string'
                ? new Date(accountData.subscription.current_period_end)
                : (accountData.subscription.current_period_end as unknown as Date);

            if (typeof accountData.subscription.current_period_end === 'string') {
              accountData.subscription.current_period_end = periodEnd;
            }
          }

          if (
            accountData.subscription?.status === 'canceled' ||
            (periodEnd && periodEnd < new Date())
          ) {
            sessionStorage.setItem(
              'login_error',
              'Your company subscription has expired or been canceled. Please contact your account administrator or support to renew access.'
            );
            await logout();
            setIsFetching(false);
            return;
          }

          const isFirstLoginDetected = !accountData.company_registration;
          setIsFirstLogin(isFirstLoginDetected);
          setHasMissingAccountInfo(isFirstLoginDetected);

          const newAccount: Account = {
            ...accountData,
            actualDefaultBucketName: accountData.actualDefaultBucketName,
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified,
            phoneNumber: user.phoneNumber,
          } as Account;

          setAccount(newAccount);
          setFetchedUid(user.uid);
        } else {
          throw new Error('Account details not found.');
        }
      } catch (_error) {
        if (!isMounted) {
          return;
        }
        setAccount(null);
        setHasMissingAccountInfo(true);
        setFetchedUid(user.uid); // Mark as fetched so we don't loop forever
      } finally {
        if (isMounted) {
          setIsFetching(false);
        }
      }
    };

    fetchInitialAccount();

    return () => {
      isMounted = false;
    };
  }, [user, account?.uid, fetchedUid, logout]);

  useEffect(() => {
    if (account) {
      storageService.setClientId(account.clientId);
      if (account.storageType) {
        storageService.setStorageType(account.storageType as 'firebase' | 'sharepoint');
      }
    } else {
      storageService.setClientId('');
    }
  }, [account]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!account) {
        return false;
      }
      return account.cachedPermissions?.includes(permission) || false;
    },
    [account]
  );

  const value: AccountContextType = {
    account,
    hasMissingAccountInfo,
    setHasMissingAccountInfo,
    isFirstLogin,
    setIsFirstLogin,
    isAccountLoading,
    refreshUserDetails,
    hasPermission,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextType => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
